import React, { useRef, useCallback } from 'react';
import { TextField, Typography } from '@mui/material';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LIBRARIES,
  isGoogleMapsConfigured,
} from '../../../../config/googleMaps';
import { mergeFieldSx } from './manageEventFieldStyles';
import { applyGooglePlaceToForm } from '../applyGooglePlace';

const LocationAutocomplete = ({
  value,
  onChange,
  onPlaceSelected,
  error,
  helperText,
  placeholder = 'Search address',
}) => {
  const autocompleteRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const handleLoad = useCallback((autocomplete) => {
    autocompleteRef.current = autocomplete;
  }, []);

  const handlePlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace();
    if (!place) return;

    const fields = applyGooglePlaceToForm(place);
    onPlaceSelected(fields);
  }, [onPlaceSelected]);

  const handleInputChange = (event) => {
    onChange(event.target.value);
  };

  if (!isGoogleMapsConfigured()) {
    return (
      <TextField
        fullWidth
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        error={Boolean(error)}
        helperText={
          helperText || 'Add REACT_APP_GOOGLE_MAPS_API_KEY to .env and restart the dev server'
        }
        sx={mergeFieldSx(Boolean(error))}
      />
    );
  }

  if (loadError) {
    return (
      <TextField
        fullWidth
        value={value}
        disabled
        error
        helperText="Failed to load Google Maps. Check your API key and enabled APIs."
        sx={mergeFieldSx(true)}
      />
    );
  }

  if (!isLoaded) {
    return (
      <TextField
        fullWidth
        value={value}
        disabled
        placeholder="Loading Google Places…"
        sx={mergeFieldSx(false)}
      />
    );
  }

  return (
    <>
      <Autocomplete
        onLoad={handleLoad}
        onPlaceChanged={handlePlaceChanged}
        options={{
          fields: ['formatted_address', 'geometry', 'name', 'place_id'],
        }}
      >
        <TextField
          fullWidth
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          error={Boolean(error)}
          helperText={helperText}
          sx={mergeFieldSx(Boolean(error))}
        />
      </Autocomplete>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
        Start typing and select an address from the list
      </Typography>
    </>
  );
};

export default LocationAutocomplete;
